import z from 'zod';
import { DBPermissionGroupGroupIdSchema } from '../permissionGroups/schemas';
import { PermissionSourceType } from '../permissions/enums';
import {
  DBPermissionPermissionIdSchema,
  PermissionMeaningSchema,
  PermissionSourceSchema,
  PermissionValueSchema,
} from '../permissions/schemas';
import { UserIdSchema } from '../users/schemas';

/**
 * Shared & Helper Schemas
 */

export const DBUserPermissionSourceSchema = z.union([
  z.lazy(() => PermissionSourceSchema),
  z.lazy(() => DBPermissionGroupGroupIdSchema),
]);

const GroupSourceSchema = <T extends PermissionSourceType>(type: T) =>
  z.object({
    type: z.literal(type),
    permissionGroupId: z.lazy(() => DBPermissionGroupGroupIdSchema),
  });

export const UserPermissionSourceSchema = z
  .discriminatedUnion('type', [
    z.object({ type: z.literal(PermissionSourceType.Direct) }),
    GroupSourceSchema(PermissionSourceType.Custom),
    GroupSourceSchema(PermissionSourceType.Class),
  ])
  .openapi({ description: 'Where the user holds the permission from' });

export const DBUserPermissionGenericPermissionsSchema = z.record(
  z.lazy(() => DBPermissionPermissionIdSchema),
  z.object({
    sources: z.array(DBUserPermissionSourceSchema),
  })
);

export const UserPermissionGenericPermissionsSchema = z.array(
  z.object({
    permissionId: z.lazy(() => PermissionValueSchema),
    ...PermissionMeaningSchema.shape,
    sources: z.array(UserPermissionSourceSchema),
  })
);

/**
 * DB Schemas
 */

export const DBUserPermissionUserIdSchema = z.lazy(() => UserIdSchema);
export const DBUserPermissionPermissionsSchema = DBUserPermissionGenericPermissionsSchema;
export const DBUserPermissionExamPermissionsSchema = DBUserPermissionGenericPermissionsSchema;
export const DBUserPermissionNotePermissionsSchema = DBUserPermissionGenericPermissionsSchema;
export const DBUserPermissionVideoPermissionsSchema = DBUserPermissionGenericPermissionsSchema;
export const DBUserPermissionTutorialPermissionsSchema = DBUserPermissionGenericPermissionsSchema;
export const DBUserPermissionPermissionGroupsSchema = z.record(
  z.lazy(() => DBPermissionGroupGroupIdSchema),
  z.object({}).strict()
);

export const DBUserPermissionSchema = z.object({
  userId: DBUserPermissionUserIdSchema,
  // .optional() indicates to ElectroDB that keys of these dynamic fields may be deleted
  permissions: DBUserPermissionPermissionsSchema.optional(),
  examPermissions: DBUserPermissionExamPermissionsSchema.optional(),
  notePermissions: DBUserPermissionNotePermissionsSchema.optional(),
  videoPermissions: DBUserPermissionVideoPermissionsSchema.optional(),
  tutorialPermissions: DBUserPermissionTutorialPermissionsSchema.optional(),
  permissionGroups: DBUserPermissionPermissionGroupsSchema.optional(),
});

/**
 * Client Schemas
 */
export const UserPermissionUserIdSchema = DBUserPermissionUserIdSchema;
export const UserPermissionPermissionsSchema = UserPermissionGenericPermissionsSchema;
export const UserPermissionExamPermissionsSchema = UserPermissionGenericPermissionsSchema;
export const UserPermissionNotePermissionsSchema = UserPermissionGenericPermissionsSchema;
export const UserPermissionVideoPermissionsSchema = UserPermissionGenericPermissionsSchema;
export const UserPermissionTutorialPermissionsSchema = UserPermissionGenericPermissionsSchema;
export const UserPermissionPermissionGroupIdsSchema = z.array(
  z.lazy(() => DBPermissionGroupGroupIdSchema)
);

export const UserPermissionSchema = z.object({
  userId: UserPermissionUserIdSchema,
  permissions: UserPermissionPermissionsSchema.default([]),
  examPermissions: UserPermissionExamPermissionsSchema.default([]),
  notePermissions: UserPermissionNotePermissionsSchema.default([]),
  videoPermissions: UserPermissionVideoPermissionsSchema.default([]),
  tutorialPermissions: UserPermissionTutorialPermissionsSchema.default([]),
  permissionGroupIds: UserPermissionPermissionGroupIdsSchema.default([]),
});
